import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { selectIsAuthenticated } from './store/slices/authSlice';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailsPage from './pages/ProjectDetailsPage';
import ProjectBoardPage from './pages/ProjectBoardPage';
import TaskDetailsPage from './pages/TaskDetailsPage';
import TasksPage from './pages/TasksPage';
import TeamsPage from './pages/TeamsPage';
import TeamDetailsPage from './pages/TeamDetailsPage';
import UsersPage from './pages/UsersPage';
import UserDetailsPage from './pages/UserDetailsPage';
import UserLevelsPage from './pages/UserLevelsPage';
import SkillsPage from './pages/SkillsPage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ResourcesPage from './pages/ResourcesPage';
import ResourceCalendarPage from './pages/ResourceCalendarPage';
import CalendarPage from './pages/CalendarPage';
import GanttPage from './pages/GanttPage';
import Layout from './components/Layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { useRealTimeUpdates } from './hooks/useRealTimeUpdates';

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
  },
});

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [darkMode, setDarkMode] = React.useState(false);

  // Enable real-time updates
  useRealTimeUpdates(isAuthenticated);

  const theme = darkMode ? darkTheme : lightTheme;

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </ErrorBoundary>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
          <Box sx={{ flexGrow: 1, p: 3 }}>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:projectId" element={<ProjectDetailsPage />} />
              <Route path="/projects/:projectId/board" element={<ProjectBoardPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/tasks/:taskId" element={<TaskDetailsPage />} />
              <Route path="/teams" element={<TeamsPage />} />
              <Route path="/teams/:teamId" element={<TeamDetailsPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/:userId" element={<UserDetailsPage />} />
              <Route path="/user-levels" element={<UserLevelsPage />} />
              <Route path="/skills" element={<SkillsPage />} />
              <Route path="/time-tracking" element={<TimeTrackingPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/gantt" element={<GanttPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/resources/timeline" element={<ResourcesPage />} />
              <Route path="/resources/:resourceId/calendar" element={<ResourceCalendarPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </ErrorBoundary>
          </Box>
        </Layout>
      </Router>
    </ThemeProvider>
  );
}

export default App;